// /app/frontend/src/utils/i18n.js
// Multi-language internationalization system

import de from '../locales/de.json';
import en from '../locales/en.json';
import ru from '../locales/ru.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';
import es from '../locales/es.json';

// All available translations
const translations = { de, en, ru, fr, it, es };

// Supported languages with native names and flags
export const SUPPORTED_LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

// Default language
export const DEFAULT_LANGUAGE = 'en';

/**
 * Detect browser language and return supported language code
 */
export function detectBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang?.split('-')[0]?.toLowerCase();
  
  // Check if browser language is supported
  const isSupported = SUPPORTED_LANGUAGES.some(l => l.code === langCode);
  return isSupported ? langCode : DEFAULT_LANGUAGE;
}

/**
 * Get translation value by dot-notation key
 * @param {string} lang - Language code (de, en, ru, fr, it, es)
 * @param {string} key - Dot-notation key (e.g., "nav.dashboard", "flashcards.title")
 * @returns {string} Translated text or key if not found
 */
export function getTranslation(lang, key) {
  const langData = translations[lang] || translations[DEFAULT_LANGUAGE];
  const fallbackData = translations[DEFAULT_LANGUAGE];
  
  // Split key by dots and traverse object
  const keys = key.split('.');
  let value = langData;
  let fallbackValue = fallbackData;
  
  for (const k of keys) {
    value = value?.[k];
    fallbackValue = fallbackValue?.[k];
  }
  
  // Return value, fallback to English, or return key
  if (value !== undefined && value !== null) return value;
  if (fallbackValue !== undefined && fallbackValue !== null) return fallbackValue;
  return key;
}

/**
 * Create a translation function for a specific language
 * @param {string} lang - Language code
 * @returns {function} Translation function t(key)
 */
export function createTranslator(lang) {
  return (key, params = {}) => {
    let text = getTranslation(lang, key);
    
    // Replace placeholders like {name} with params
    Object.entries(params).forEach(([param, value]) => {
      text = text.replace(new RegExp(`{${param}}`, 'g'), value);
    });
    
    return text;
  };
}

/**
 * Get all translations for a language
 * @param {string} lang - Language code
 * @returns {object} All translations for the language
 */
export function getAllTranslations(lang) {
  return translations[lang] || translations[DEFAULT_LANGUAGE];
}

export default translations;
